import React, { useMemo, useState } from "react";
import { Search, Users } from "lucide-react";
import ReactSelect, { SingleValue } from "react-select";

import PageHeader from "../../components/PageHeader";
import { SELECT_STYLES } from "../../components/DateFilters/constants";

import { RoteristaProvider, useRoterista } from "./context/RoteristaContext";
import ChamarProximoButton from "./components/ChamarProximoButton";
import StatsCards from "./components/StatsCards";
import FilaEntregadores from "./components/FilaEntregadores";
import EntregadoresEmEntrega from "./components/EntregadoresEmEntrega";
import EntregasRealizadasHoje from "./components/EntregasRealizadasHoje";
import PedidosAguardandoEntrega from "./components/PedidosAguardandoEntrega";
import ModalChamada from "./components/ModalChamada";
import ModalBuscarPedido from "./components/ModalBuscarPedido";
import ModalEntregadores from "./components/ModalEntregadores";
import RoteristaPasswordGate from "./components/RoteristaPasswordGate";

const RoteristaContent: React.FC = () => {
  const [isModalEntregadoresOpen, setIsModalEntregadoresOpen] = useState(false);
  const [isModalBuscarPedidoOpen, setIsModalBuscarPedidoOpen] = useState(false);

  const {
    lojaId,
    lojas,
    selectedLojaId,
    setSelectedLojaId,
    showLojaSelector,
    isLoading,
    entregadoresNaFila,
    entregadoresEmEntrega,
    proximoChamado,
    enderecos,
    stats,
    isChamando,
    isConfirmandoSaida,
    isFinalizandoDia,
    handleChamarProximo,
    handleChamarEntregador,
    handleConfirmarSaida,
    handleMoverParaFim,
    handleChamarNovamente,
    handleCloseModal,
    handleFinalizarDia,
    setEnderecos,
    usaIntegracaoPedidos,
    atualizarPedidosEntrega,
    handleFinalizarEntrega,
    finalizandoEntregaId,
    numerosPermitidosNaFila,
  } = useRoterista();

  const lojaOptions = useMemo(
    () => lojas.map((loja) => ({ value: loja.id, label: loja.nome })),
    [lojas]
  );
  const selectedLojaOption =
    lojaOptions.find((o) => o.value === selectedLojaId) ?? null;

  const renderLojaSelector = (inputId: string) =>
    showLojaSelector &&
    lojas.length > 0 && (
      <div className="flex flex-wrap items-center gap-2">
        <label
          htmlFor={inputId}
          className="text-sm font-medium text-neutral-700"
        >
          Loja:
        </label>
        <ReactSelect<{ value: number; label: string }>
          inputId={inputId}
          value={selectedLojaOption}
          onChange={(option: SingleValue<{ value: number; label: string }>) =>
            setSelectedLojaId(option?.value ?? undefined)
          }
          options={lojaOptions}
          isClearable
          placeholder="Selecione uma loja"
          className="w-full min-w-[140px] sm:w-56"
          styles={SELECT_STYLES}
        />
      </div>
    );

  const lojaWarning = showLojaSelector && !lojaId && (
    <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800 sm:px-4 sm:text-sm">
      Selecione uma loja para visualizar a fila de entregadores.
    </p>
  );

  const modals = (
    <>
      {proximoChamado && (
        <ModalChamada
          isOpen={!!proximoChamado}
          onClose={handleCloseModal}
          entregador={proximoChamado}
          enderecos={enderecos}
          onEnderecosChange={setEnderecos}
          onConfirmar={handleConfirmarSaida}
          onMoverFim={handleMoverParaFim}
          onChamarNovamente={handleChamarNovamente}
          usaIntegracaoPedidos={usaIntegracaoPedidos}
          numerosPermitidosNaFila={numerosPermitidosNaFila}
          isConfirmando={isConfirmandoSaida}
        />
      )}

      <ModalEntregadores
        isOpen={isModalEntregadoresOpen}
        onClose={() => setIsModalEntregadoresOpen(false)}
        entregadores={entregadoresNaFila}
        onFinalizarDia={handleFinalizarDia}
        isFinalizando={isFinalizandoDia}
      />

      <ModalBuscarPedido
        isOpen={isModalBuscarPedidoOpen}
        onClose={() => setIsModalBuscarPedidoOpen(false)}
        lojaId={lojaId}
      />
    </>
  );

  if (isLoading) {
    return (
      <main className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
        <PageHeader
          title="Dashboard Roteirista"
          description="Gerenciar fila de entregadores"
        />
        <div className="mt-6 flex items-center justify-center py-8">
          <div className="flex flex-col items-center gap-2">
            <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary"></div>
            <p className="text-neutral-500">Carregando...</p>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
      <PageHeader
        title="Dashboard Roteirista"
        description="Gerenciar fila de entregadores"
      />

      {/*
        Compacto em desktop com janela até 940px:
        max-width ≤ 940px AND pointer:fine.
        Mobile/tablet (pointer:coarse) sempre usam o layout legado abaixo.
      */}
      <div className="mt-4 hidden max-h-[calc(100vh-8rem)] gap-2 [@media(max-width:940px)_and_(pointer:fine)]:flex">
        <div className="min-w-0 flex-1 space-y-3 overflow-y-auto pb-2">
          {renderLojaSelector("roterista-loja-select-compact")}
          {lojaWarning}

          <div className="flex items-stretch gap-1.5">
            <div className="min-w-0 flex-1">
              <ChamarProximoButton
                compact
                disabled={entregadoresNaFila.length === 0}
                loading={isChamando}
                onClick={handleChamarProximo}
              />
            </div>
            <button
              type="button"
              onClick={() => setIsModalBuscarPedidoOpen(true)}
              aria-label="Buscar pedido"
              title="Buscar pedido"
              className="flex shrink-0 items-center justify-center rounded-lg border-2 border-primary/40 bg-primary-light/15 px-2.5 text-primary transition-colors hover:bg-primary-light/25"
            >
              <Search size={18} />
            </button>
          </div>

          <button
            type="button"
            onClick={() => setIsModalEntregadoresOpen(true)}
            className="flex w-full items-center justify-center gap-2 rounded-lg border-2 border-neutral-300 bg-white px-3 py-2 text-sm font-semibold text-neutral-700 transition-colors hover:border-neutral-400 hover:bg-neutral-50"
          >
            <Users size={16} />
            Entregadores
          </button>

          <StatsCards stats={stats} compact />

          <FilaEntregadores
            entregadores={entregadoresNaFila}
            onChamarEntregador={handleChamarEntregador}
          />

          <EntregadoresEmEntrega
            entregadores={entregadoresEmEntrega}
            onAtualizarPedidos={atualizarPedidosEntrega}
            onFinalizarEntrega={handleFinalizarEntrega}
            finalizandoEntregaId={finalizandoEntregaId}
          />

          <EntregasRealizadasHoje lojaId={lojaId} />
        </div>

        <div className="flex w-[48%] min-w-0 shrink-0 flex-col">
          <PedidosAguardandoEntrega fillHeight />
        </div>
      </div>

      {/* Layout legado: mobile, tablet e desktop largo */}
      <div className="mt-6 space-y-6 [@media(max-width:940px)_and_(pointer:fine)]:hidden">
        {renderLojaSelector("roterista-loja-select")}
        {lojaWarning}

        <div className="flex flex-wrap items-center gap-3">
          <ChamarProximoButton
            disabled={entregadoresNaFila.length === 0}
            loading={isChamando}
            onClick={handleChamarProximo}
          />
          <button
            type="button"
            onClick={() => setIsModalBuscarPedidoOpen(true)}
            className="flex items-center gap-2 rounded-lg border-2 border-primary/40 bg-primary-light/15 px-6 py-3 font-semibold text-primary transition-colors hover:bg-primary-light/25"
          >
            <Search size={20} />
            Buscar pedido
          </button>
          <button
            type="button"
            onClick={() => setIsModalEntregadoresOpen(true)}
            className="flex items-center gap-2 rounded-lg border-2 border-neutral-300 bg-white px-6 py-3 font-semibold text-neutral-700 transition-colors hover:border-neutral-400 hover:bg-neutral-50"
          >
            <Users size={20} />
            Entregadores
          </button>
        </div>

        <StatsCards stats={stats} />

        <div className="grid gap-6 lg:grid-cols-2">
          <div className="lg:col-span-1">
            <FilaEntregadores
              entregadores={entregadoresNaFila}
              onChamarEntregador={handleChamarEntregador}
            />
          </div>

          <div className="lg:col-span-1">
            <EntregadoresEmEntrega
              entregadores={entregadoresEmEntrega}
              onAtualizarPedidos={atualizarPedidosEntrega}
              onFinalizarEntrega={handleFinalizarEntrega}
              finalizandoEntregaId={finalizandoEntregaId}
            />
          </div>
        </div>

        <div className="grid gap-6 lg:grid-cols-2">
          <PedidosAguardandoEntrega />
          <EntregasRealizadasHoje lojaId={lojaId} />
        </div>
      </div>

      {modals}
    </main>
  );
};

const Roterista: React.FC = () => {
  return (
    <RoteristaPasswordGate>
      <RoteristaProvider>
        <RoteristaContent />
      </RoteristaProvider>
    </RoteristaPasswordGate>
  );
};

export default Roterista;
